import { get } from '@github/webauthn-json';
import { useEffect, useRef } from 'react';
import { browserSupportsPasskeys } from '../../pages/settings/passkeys/utils';
import { PasskeyLoginEndpointScope, getPasskeyLoginOptions, loginWithPasskey } from '../../utils/requests/clientAuth/passkeys';
import { SignInMachineContext } from '../../machines/sign-in/SignInMachineContext';
import { isAndroidApp } from '../../utils/registration/android';
import { isPasskeyLoginAuthError } from '../../utils/requests/clientAuth/login/types';
import { browserLogger } from '../../utils/loggers/browser';
const usePasskeySignIn = () => {
  const [state, send] = SignInMachineContext.useActor();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isWafEnabled = state.context.isWafEnabled;
  const scope = isAndroidApp() ? PasskeyLoginEndpointScope.android : PasskeyLoginEndpointScope.web;
  const handleAbort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };
  useEffect(() => {
    const initiatePasskeyLogin = async () => {
      const canUsePasskeys = await browserSupportsPasskeys();
      if (!canUsePasskeys) return;
      try {
        handleAbort();
        abortControllerRef.current = new AbortController();
        const {
          data: publicKey
        } = await getPasskeyLoginOptions({
          scope
        });
        const credential = await get({
          publicKey,
          mediation: 'conditional',
          signal: abortControllerRef.current.signal
        });
        const loginResponse = await loginWithPasskey({
          credential,
          scope
        });
        if (loginResponse.status === 201) {
          browserLogger({
            level: 'info',
            team: 'client-identity-access-management',
            feature: 'passkeys',
            message: 'Passkey Sign In Success - updating state to PASSKEY_LOGIN_SUCCESS'
          });
          send({
            type: 'PASSKEY_LOGIN_SUCCESS',
            redirectUrl: loginResponse.data.redirect_url || '/home'
          });
        }
      } catch (error: unknown) {
        const errorMessage = isPasskeyLoginAuthError(error) ? error.response.data.error.message : (error as DOMException).message;
        const fallbackUserFacingMessage = "Something went wrong. Please make sure you've added a passkey to your account.";
        browserLogger({
          level: 'info',
          team: 'client-identity-access-management',
          feature: 'passkeys',
          message: 'Passkey Sign In Error - updating state to PASSKEY_LOGIN_FAILURE',
          context: {
            errorMessage,
            isWafEnabled
          }
        });
        send({
          type: 'PASSKEY_LOGIN_FAILURE',
          error: {
            error_message: isPasskeyLoginAuthError(error) ? errorMessage : fallbackUserFacingMessage,
            recaptcha: false
          },
          isWafEnabled
        });
      }
    };
    initiatePasskeyLogin();
    return () => {
      handleAbort();
    };
  }, [isWafEnabled, scope, send]);
};
export default usePasskeySignIn;